import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, MoreVertical, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronRight, SlidersHorizontal, Users } from "lucide-react";
import { PropertyDetailCard } from "@/components/pages/properties/PropertyDetailCard";
import { PropertyFormDialog } from "@/components/pages/properties/PropertyFormDialog";
import {
  useGetPropertyQuery,
  useGetPropertyContactsQuery,
  useDeletePropertyMutation,
  useUpdatePropertyMutation,
} from "@/store/rest_api";
import CustomFieldsEditor from "@/components/CustomFieldsEditor";
import { apiErrorMessage } from "@/util/apiError";
import { ContactType } from "@/enum/ContactType";

export default function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEditDialog, setShowEditDialog] = useState(false);

  const { data: property, isLoading } = useGetPropertyQuery(id!);
  const { data: linkedContacts = [] } = useGetPropertyContactsQuery(id!);
  const [deleteProperty] = useDeletePropertyMutation();
  const [updateProperty] = useUpdatePropertyMutation();

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this property?")) {
      return;
    }
    try {
      await deleteProperty(id!).unwrap();
      toast.success("Property deleted");
      navigate("/properties");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete property"));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4 max-w-5xl">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Property not found</p>
      </div>
    );
  }

  const location = [property.city, property.state, property.zip].filter(Boolean).join(", ");

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate("/properties")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold truncate">{property.address}</h1>
            {location && <p className="text-sm text-muted-foreground">{location}</p>}
          </div>
          {property.listingUrl && (
            <a
              href={property.listingUrl}
              target="_blank"
              rel="noreferrer"
              className="flex shrink-0 items-center gap-1 text-sm text-primary hover:underline"
            >
              View original listing
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowEditDialog(true)}>
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" title="Options">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-destructive" onClick={handleDelete}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete property
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <PropertyDetailCard property={property} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            Contacts ({linkedContacts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {linkedContacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No contacts linked to this property yet.</p>
          ) : (
            <div className="divide-y">
              {linkedContacts.map((contact) => {
                const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ") || "Unnamed contact";
                const phone = contact.phones?.[0]?.phoneNumber;
                return (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() =>
                      navigate(
                        contact.type === ContactType.LEAD
                          ? `/leads/${contact.id}`
                          : `/contacts/${contact.id}/detail`,
                      )
                    }
                    className="flex w-full items-center justify-between gap-3 py-3 text-left hover:bg-muted/50 rounded-md px-2 -mx-2"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        {name}
                        {contact.type && <Badge variant="outline" className="text-[10px]">{contact.type}</Badge>}
                      </div>
                      {phone && <div className="text-xs text-muted-foreground">{phone}</div>}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <SlidersHorizontal className="h-4 w-4" />
            Custom fields
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CustomFieldsEditor
            fields={property.customFields}
            onSave={(customFields) => updateProperty({ id: property.id, data: { ...property, customFields } }).unwrap()}
          />
        </CardContent>
      </Card>

      <PropertyFormDialog open={showEditDialog} onOpenChange={setShowEditDialog} property={property} />
    </div>
  );
}
